import { useState } from "react";
import { FileMetadata } from "src/requests/files/types";
import { FileConfigParams, RawFile } from "../types";
import {
  downloadFile,
  getRawFilesAndFilesMetadataFromFileList,
  isFileRaw,
  openFile,
  useUniqueIdentifier,
} from "../utils";
import useAddFileActionsToQueue from "./useAddFileActionsToQueue";
import useFiles from "./useFiles";

type ReturnType = {
  files: FileMetadata[];
  areFilesLoading: boolean;
  uploadFiles(newFiles: FileList): void;
  deleteFile(fileId: number | string): void;
  openFile(file: FileMetadata): void;
  downloadFile(file: FileMetadata): void;
  fetchFilesOnOpen(): void;
};

type UseFilesAndItsActions = (params: FileConfigParams) => ReturnType;

const useFilesAndItsActions: UseFilesAndItsActions = (params) => {
  const { templateId, templateItemId, recordId, editMode } = params;

  const [addedFiles, setAddedFiles] = useState<RawFile[]>([]);
  const [removedFiles, setRemovedFiles] = useState<FileMetadata[]>([]);
  const uniqueIdentifier = useUniqueIdentifier();

  const {
    addDeleteToActionsQueue,
    addUploadToActionsQueue,
    removeActionsForRowFromQueue,
  } = useAddFileActionsToQueue(uniqueIdentifier, params);

  const clearLocalData = (): void => {
    setAddedFiles([]);
    setRemovedFiles([]);
    removeActionsForRowFromQueue();
  };

  const { files, setFiles, areFilesFetching, fetchFilesOnOpen } = useFiles({
    recordId,
    templateId,
    templateItemId,
    editMode,
    clearLocalData,
  });

  const uploadFiles = (newFiles: FileList): void => {
    const { rawFiles, filesMetadata } =
      getRawFilesAndFilesMetadataFromFileList(newFiles);

    const filesToAdd = [...addedFiles, ...rawFiles];

    setFiles([...files, ...filesMetadata]);
    setAddedFiles(filesToAdd);
    addUploadToActionsQueue(filesToAdd);
  };

  const deleteFile = (fileId: number | string): void => {
    const filterFileFromFiles = (file: FileMetadata): boolean =>
      file.id !== fileId;

    const filerFileFromAddedFiles = (file: RawFile): boolean =>
      file.id !== fileId;

    if (!isFileRaw(fileId)) {
      const fileToDelete = files.find((f) => f.id === fileId);

      if (fileToDelete) {
        const filesToDelete = [...removedFiles, fileToDelete];
        setRemovedFiles(filesToDelete);
        addDeleteToActionsQueue(filesToDelete);
      }
    }

    setAddedFiles(addedFiles.filter(filerFileFromAddedFiles));
    setFiles(files.filter(filterFileFromFiles));
  };

  const openFileWrapper = (file: FileMetadata): void => {
    openFile({ file, templateItemId, templateId });
  };

  const downloadFileWrapper = (file: FileMetadata): void => {
    downloadFile({ file, templateId, templateItemId });
  };

  return {
    uploadFiles,
    deleteFile,
    openFile: openFileWrapper,
    downloadFile: downloadFileWrapper,
    fetchFilesOnOpen,
    files,
    areFilesLoading: areFilesFetching && !files.length,
  };
};

export default useFilesAndItsActions;
